/**
 * One booking as a table row.
 *
 * The row is the link to the booking; the workshop and venue names are their
 * own links into the current dashboards shell and stop propagation so a click
 * on either does not also open the booking.
 */
import { useNavigate } from 'react-router-dom';

import type { BookingListItem } from '../../../api/bookingsList';
import {
  shellBasePath,
  workshopDetailUrlFor,
  type WorkshopShell,
} from '../../dashboards/workshopDetailUrlFor';
import {
  customerName,
  displaySource,
  displayTotal,
  workshopDateLabel,
  workshopTimeLabel,
} from '../bookingListModel';
import { LINK } from '../bookingListTableStyle';
import { BookingStatusPill } from '../BookingStatusPill';
import { ShellLink } from '../ShellLink';

export default function BookingTableRow({
  b,
  shell,
}: {
  b: BookingListItem;
  shell: WorkshopShell;
}) {
  const navigate = useNavigate();
  const w = b.workshop;
  const go = () => navigate(`/admin/bookings/${b.id}`);
  const workshopHref = w.id ? workshopDetailUrlFor(shell, w.id) : null;
  const venueHref = w.venue.id ? `${shellBasePath(shell)}/venues/${w.venue.id}` : null;
  return (
    <tr
      role="button"
      tabIndex={0}
      aria-label={`Open booking ${b.displayRef ?? customerName(b.customer)}`}
      onClick={go}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          go();
        }
      }}
      className="cursor-pointer transition hover:bg-moss-50/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-moss-500 dark:hover:bg-[#242d28]"
    >
      <td className="px-4 py-3 font-mono text-xs text-stone-500">{b.displayRef ?? '—'}</td>
      <td className="px-4 py-3">
        <div className="font-semibold text-bark-900 dark:text-[#e8ece9]">
          {customerName(b.customer)}
        </div>
        <div className="text-xs text-stone-500">{b.customer.email ?? '—'}</div>
      </td>
      <td className="px-4 py-3">
        <div className="font-semibold text-bark-900 dark:text-stone-100">
          <ShellLink href={workshopHref} className={LINK}>
            {w.name ?? 'Workshop'}
          </ShellLink>
        </div>
        <div className="text-xs text-stone-500">
          {workshopDateLabel(w)} · {workshopTimeLabel(w)} ·{' '}
          <ShellLink href={venueHref} className={LINK}>
            {w.venue.name}
          </ShellLink>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="font-semibold text-bark-900 dark:text-stone-100">{w.region.name}</div>
        {w.region.parentName && <div className="text-xs text-stone-500">{w.region.parentName}</div>}
      </td>
      <td className="px-4 py-3 font-medium text-bark-900 dark:text-stone-100">{b.attendeeCount}</td>
      <td className="px-4 py-3 text-stone-600 dark:text-stone-400">{displaySource(b)}</td>
      <td className="px-4 py-3">
        <BookingStatusPill booking={b} />
      </td>
      <td className="px-4 py-3 text-right font-semibold text-bark-900 dark:text-[#e8ece9]">
        {displayTotal(b)}
      </td>
    </tr>
  );
}
